/**
 * Axios API конфигурация с interceptors
 * Базовая настройка HTTP клиента
 */

import axios, { 
  type AxiosInstance, 
  type AxiosError, 
  type InternalAxiosRequestConfig 
} from 'axios'
import { useAuthStore } from '@/stores/auth'
import router from '@/router'

const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// ==================== REQUEST INTERCEPTOR ====================

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore()
    const token = authStore.accessToken
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.params || config.data)
    }
    
    return config
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// ==================== RESPONSE INTERCEPTOR ====================

api.interceptors.response.use(
  (response) => {
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data)
    }
    
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    
    // Обработка 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      // Просто редирект на логин, без попытки refresh
      // Очищаем только токены, без вызова API logout
      const authStore = useAuthStore()
      authStore.accessToken = null
      authStore.refreshToken = null
      authStore.user = null
      localStorage.removeItem('dustar_access_token')
      localStorage.removeItem('dustar_refresh_token')
      localStorage.removeItem('dustar_user')
      
      router.push('/admin-login')
      return Promise.reject(error)
    }
    
    if (error.response?.status === 404) {
      console.error('[API Not Found]', error.config?.url)
    }
    
    if (error.response && error.response.status >= 500) {
      console.error('[API Server Error]', error.response.data)
    }
    
    return Promise.reject(error)
  }
)

export default api
